import React from "react"
import { useId } from "@reach/auto-id"
import i18next from "i18next"
import { useTranslation } from "react-i18next"
import { v4 as uuidv4 } from "uuid"

import {
	FormFields,
	FormRule,
	FormError,
	DocumentToUpload,
	UploadedDocument,
	DocumentsTypes,
} from "models"
import { convertFileToBlob, objectIsNotEmpty } from "utils"

import {
	handleInternalInputValidation,
	FormFieldFloatingLabel,
	StatesAndHelpersFloatingLabel,
} from "../formUtils"

import { LinkToRemoteFile, HiddenInputFile } from "./Partials"
import { UploadIcon } from "components/Elements"
import {
	getStatesAndHelpersInputFile,
	DEFAULT_ACCEPTED_EXTENSIONS,
	DEFAULT_MAX_FILE_SIZE,
	FileErrorActionType,
	Errors,
} from "components/Form"

import "./InputFile.scoped.scss"

export type InputFileProps = FormFields & {
	value: DocumentToUpload
	unit?: string
	inputStyle?: React.CSSProperties
	classNameInput?: string
	acceptedExtensions?: string[]
	maxFileSize: number
	filesType: DocumentsTypes
	// NB: defaultValue shows an existing remote file
	defaultValue?: UploadedDocument
	disabledLink?: boolean
}

export const InputFile: React.FC<InputFileProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value,
			defaultValue,
			name,
			label = "",
			placeholder = defaultValue
				? i18next.t("file_replace_placeholder", { ns: "form" })
				: i18next.t("file_add_placeholder", { ns: "form" }),
			disabled,
			readonly,
			disabledLink = false, // the readonly file link can also be disabled
			filesType = DocumentsTypes.AUTRE,
			// Styles
			style,
			className = "",
			classNameLabel = "",
			classNameInput = "",
			forceFloating = false,
			// Error management
			acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
			maxFileSize = DEFAULT_MAX_FILE_SIZE,
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const INPUT_TYPE = "file"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const [errors, setErrors] = React.useState<FormError[]>([])
		const { t } = useTranslation()

		const { fileErrorReducer, checkForFileError } = getStatesAndHelpersInputFile({
			acceptedExtensions,
			maxFileSize,
		})

		const isDisabledOrReadonly = disabled || readonly

		const [selectedFile, setSelectedFile] = React.useState<DocumentToUpload>(value)
		const [fileError, dispatchFileError] = React.useReducer(fileErrorReducer, null)

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "input-empty",
					check: (value) => (required ? objectIsNotEmpty(value) : true),
					message: t("error_mandatory_field", {
						ns: "form",
						fieldName: label,
					}),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: any) => {
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate)
			},
			[rules, name, onValidate]
		)

		// validate on mount and each time value change
		React.useEffect(() => {
			validate(value)
			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value])

		const getLabel = () => selectedFile?.document?.custom_name ?? placeholder

		// intermediary method to provide the form's onChange function
		// with the expected value format
		const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
			const inputFileTarget = event.target as HTMLInputElement
			// the original type FileList is not considered as an array
			const file = [...(inputFileTarget.files as unknown as File[])][0]

			if (file) {
				const hasFileError = await checkForFileError(file)

				if (hasFileError) {
					dispatchFileError({
						type: hasFileError.type,
					})
				} else {
					const fileExtension = file.name.split(".").pop()
					const blob = await convertFileToBlob(file)
					const inputValue: { target: DocumentToUpload & { blob: Blob } } = {
						target: {
							bike_id: "",
							account_id: "",
							document: {
								name: `${uuidv4()}.${fileExtension}`,
								custom_name: file.name,
								gender: filesType,
							},
							blob,
						},
					}

					dispatchFileError({ type: FileErrorActionType.READY })
					setSelectedFile(inputValue.target)
					onChange && onChange(inputValue)
				}
			}
		}

		return (
			<FormFieldFloatingLabel
				inputType={INPUT_TYPE}
				inputValue={selectedFile}
				forceFloating={forceFloating}
			>
				{({
					floatingLabelClassname,
					onFloatingLabelFocus,
					onFloatingLabelBlur,
				}: StatesAndHelpersFloatingLabel) => (
					<div
						className={`c-form-field ${className} ${floatingLabelClassname}`}
						style={style}
					>
						{label && (
							<label
								className={`c-form-field__label c-label px-2 ${classNameLabel}`}
								htmlFor={inputId}
							>
								{label} {required && "*"}
							</label>
						)}

						<div className={`${classNameInput}`}>
							{!isDisabledOrReadonly && (
								<div className={"flex"}>
									<HiddenInputFile
										name={name}
										inputId={inputId}
										errorId={errorId}
										errors={errors}
										ref={ref}
										disabled={disabled}
										readonly={readonly}
										required={required}
										onChange={(e) => !readonly && onChange && handleChange(e)}
										onFocus={(event) => {
											onFloatingLabelFocus()
											onFocus && onFocus(event)
										}}
										onBlur={(event) => {
											onFloatingLabelBlur()
											onBlur && onBlur(event)
										}}
									/>

									<label
										htmlFor={inputId}
										className={`
                                        c-form-field__form c-input-file px-5
                                        ${showErrors && errors.length > 0 ? "has-error " : ""}
                                    `}
									>
										<UploadIcon size="20" />
										<div className="u-ellipsis pl-4">{getLabel()}</div>
									</label>
									{defaultValue && (
										<LinkToRemoteFile
											file={defaultValue}
											variant={"secondary"}
											disabled={disabledLink}
										/>
									)}
								</div>
							)}

							{isDisabledOrReadonly && (
								<>
									{defaultValue && (
										<LinkToRemoteFile
											file={defaultValue}
											label={t("file_show_remote_file", { ns: "form" })}
											variant={"primary"}
											disabled={disabledLink}
										/>
									)}

									{!defaultValue && (
										<span className="inline-block italic mt-2">
											{t("file_no_remote_file", { ns: "form" })}
										</span>
									)}
								</>
							)}
						</div>
						{/* Error messages */}
						{showErrors && (
							<Errors
								id={errorId}
								errors={[...errors, ...(fileError ? [fileError] : [])]}
							/>
						)}

						{!showErrors && fileError && (
							<Errors
								id={errorId}
								errors={fileError ? [fileError as unknown as FormError] : []}
							/>
						)}
					</div>
				)}
			</FormFieldFloatingLabel>
		)
	}
)
